import React, { useMemo } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { FlexBox, Text, Theme } from '@lumapps/lumx/react';
import { standardizeTranslateObject, useTranslate } from '@lumapps/translations';

import { getWidgetTypeTranslationKey } from '../../keys';
import { EmptyWidgetPlaceholderProps } from '../../types';

import './index.scss';

const CLASSNAME = 'widget-content-placeholder';

export const EmptyWidgetPlaceholder: React.FC<EmptyWidgetPlaceholderProps> = ({
    widgetType,
    customPlaceholder,
    widgetContentStyle: { paddingBottom, paddingLeft, paddingRight, paddingTop, padding, ...widgetContentStyle } = {},
    theme = Theme.light,
}) => {
    const { translateKey, translateObject } = useTranslate();
    const { block } = useClassnames(CLASSNAME);

    const paddingsOverride = useMemo(() => {
        const paddings = {
            top: padding ?? paddingTop,
            right: padding ?? paddingRight,
            bottom: padding ?? paddingBottom,
            left: padding ?? paddingLeft,
        };

        return (Object.keys(paddings) as Array<keyof typeof paddings>).reduce((acc, direction) => {
            if (paddings[direction] === undefined) {
                return acc;
            }
            return {
                ...acc,
                [`--widget-content-style-padding-${direction}`]: `${paddings[direction]}px`,
            };
        }, {});
    }, [padding, paddingBottom, paddingLeft, paddingRight, paddingTop]);

    return (
        <FlexBox
            style={{
                ...paddingsOverride,
                ...widgetContentStyle,
            }}
            className={block()}
            vAlign="center"
            hAlign="center"
            orientation="vertical"
        >
            <Text as="p" typography="subtitle1" color={theme === Theme.dark ? 'light' : 'dark'} colorVariant="L3">
                {translateKey(getWidgetTypeTranslationKey(widgetType || '')).toUpperCase()}
            </Text>
            {customPlaceholder && (
                <Text as="p" typography="body1" color={theme === Theme.dark ? 'light' : 'dark'} colorVariant="L2">
                    {translateObject(standardizeTranslateObject(customPlaceholder))}
                </Text>
            )}
        </FlexBox>
    );
};
